import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  type MeResponse,
  type TenantRole,
  type UmClient,
  type UserRole,
} from "@nexys/user-management-client";

export interface AuthSnapshot {
  user: MeResponse["user"];
  session: MeResponse["session"];
  authSource: MeResponse["authSource"];
}

export interface AuthContextValue {
  client: UmClient;
  status: "loading" | "authenticated" | "unauthenticated";
  data: AuthSnapshot | null;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  hasUserRole: (...roles: UserRole[]) => boolean;
  hasTenantRole: (...roles: TenantRole[]) => boolean;
}

const Ctx = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  client: UmClient;
  children: ReactNode;
  /** Re-poll /api/me on this interval (ms). 0 disables. Default 5min. */
  refreshIntervalMs?: number;
  /** Silently rotate JWT cookies on this interval (ms). 0 disables. Default 10min. */
  tokenRefreshIntervalMs?: number;
  /** Called after sign-out completes; consumer typically navigates to /sign-in. */
  onSignOut?: () => void;
  /** Optional fallback while we're determining the auth state. */
  fallback?: ReactNode;
}

export const AuthProvider = ({
  client,
  children,
  refreshIntervalMs = 5 * 60 * 1000,
  tokenRefreshIntervalMs = 10 * 60 * 1000,
  onSignOut,
  fallback,
}: AuthProviderProps) => {
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [data, setData] = useState<AuthSnapshot | null>(null);

  const refresh = useCallback(async () => {
    const res = await client.me();
    if (res.ok) {
      setData(res.data);
      setStatus("authenticated");
    } else {
      setData(null);
      setStatus("unauthenticated");
    }
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!refreshIntervalMs || status !== "authenticated") return;
    const id = setInterval(() => void refresh(), refreshIntervalMs);
    return () => clearInterval(id);
  }, [refresh, refreshIntervalMs, status]);

  // Silently rotate JWT cookies before the access token expires.
  useEffect(() => {
    if (!tokenRefreshIntervalMs || status !== "authenticated") return;
    const id = setInterval(() => {
      void client.refreshToken();
    }, tokenRefreshIntervalMs);
    return () => clearInterval(id);
  }, [client, tokenRefreshIntervalMs, status]);

  const signOut = useCallback(async () => {
    await client.signOut();
    setData(null);
    setStatus("unauthenticated");
    onSignOut?.();
  }, [client, onSignOut]);

  const value = useMemo<AuthContextValue>(
    () => ({
      client,
      status,
      data,
      refresh,
      signOut,
      hasUserRole: (...roles) => (data ? roles.includes(data.user.role) : false),
      hasTenantRole: (...roles) =>
        data ? data.session.tenantRoles.some((r) => roles.includes(r)) : false,
    }),
    [client, status, data, refresh, signOut]
  );

  if (status === "loading" && fallback !== undefined) {
    return <>{fallback}</>;
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
};

export const useUmClient = (): UmClient => useAuth().client;
